import { Box, useTheme } from '@mui/material';
import { DefinitionForType, ResourceType, isLoadingStatus, useCollection, useCollectionStatus } from 'json-api-hooks';
import React, { FC, Fragment, ReactElement, useContext } from 'react';

import HeadingContext from '../Heading/HeadingContext';

import { CollectionContext } from './Collection';

interface CollectionItemsProps<T extends ResourceType> {
  children: (props: DefinitionForType<T>, index?: number) => ReactElement | null;
  onEmpty?: FC;
  onLoad?: FC;
}

const CollectionItems = <T extends ResourceType>({
  children,
  onEmpty: OnEmpty,
  onLoad: OnLoad,
}: CollectionItemsProps<T>) => {
  const theme = useTheme();
  const { collectionOpts, resourceType } = useContext(CollectionContext);
  const [items] = useCollection(resourceType, collectionOpts);
  const requestedCollectionStatus = useCollectionStatus(resourceType, collectionOpts);

  if (isLoadingStatus(requestedCollectionStatus)) {
    return OnLoad ? <OnLoad /> : <div />;
  }

  if (OnEmpty && items.length === 0) {
    return (
      <Box component="span" sx={{ color: theme.palette.grey.midDark }}>
        <OnEmpty />
      </Box>
    );
  }

  return (
    <HeadingContext>
      {items.map((item, index) => (
        <Fragment key={item.id}>{children(item as DefinitionForType<T>, index)}</Fragment>
      ))}
    </HeadingContext>
  );
};

export default CollectionItems;
